/**
 * MapData — connected node-graph map for the Word RPG.
 *
 * Unlike a Slay the Spire linear floor map, this is a web of
 * interconnected nodes the player can traverse freely.
 * Connections are bidirectional — you can go back and forth.
 *
 * Node count is configurable. The generator distributes room types
 * across a grid with jitter, then connects nearby nodes into a web.
 */

export type RoomType = "battle" | "elite" | "shop" | "treasure" | "rest" | "event" | "boss";

export interface MapNode {
  id: string;
  type: RoomType;
  connections: string[]; // bidirectional — connected node ids
  x: number;
  y: number;
  label?: string; // optional display name override
}

export interface GameMap {
  nodes: Map<string, MapNode>;
  startNodeId: string;
  /** World-space bounds for the map (for camera). */
  width: number;
  height: number;
}

/** Default number of nodes. */
export const DEFAULT_NODE_COUNT = 50;

/** Seeded RNG — simple mulberry32 */
function seededRng(seed: number): () => number {
  let s = seed | 0;
  return () => {
    s = (s + 0x6d2b79f5) | 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Shuffle array in-place using the seeded rng. */
function shuffle<T>(arr: T[], rng: () => number): T[] {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

/** Pick a room type weighted by progression (0 = start, 1 = boss area). */
function pickRoomType(progress: number, rng: () => number): RoomType {
  // Near the start: mostly battles and events
  // Middle: mixed
  // Near the end: more elites, leading to boss
  const r = rng();

  if (progress < 0.15) {
    // Early zone
    if (r < 0.6) return "battle";
    if (r < 0.85) return "event";
    return "treasure";
  } else if (progress < 0.7) {
    // Mid zone
    if (r < 0.30) return "battle";
    if (r < 0.45) return "elite";
    if (r < 0.60) return "shop";
    if (r < 0.75) return "treasure";
    if (r < 0.90) return "rest";
    return "event";
  } else {
    // Late zone (not boss node itself)
    if (r < 0.35) return "elite";
    if (r < 0.55) return "battle";
    if (r < 0.70) return "rest";
    if (r < 0.85) return "shop";
    return "event";
  }
}

/**
 * Generate a connected graph map with a configurable number of nodes.
 *
 * Layout: nodes are placed in a grid with jitter, then connected
 * to nearby neighbours. The start node is bottom-centre, the boss
 * node is top-centre.
 */
export function generateMap(seed: number, nodeCount: number = DEFAULT_NODE_COUNT): GameMap {
  const rng = seededRng(seed);
  const nodes = new Map<string, MapNode>();

  // --- Layout dimensions ---
  // Scale world size with node count so nodes don't overlap
  const cols = Math.ceil(Math.sqrt(nodeCount * 1.5));
  const rows = Math.ceil(nodeCount / cols);

  const cellW = 100;
  const cellH = 90;
  const padX = 80;
  const padY = 60;

  const worldW = padX * 2 + cols * cellW;
  const worldH = padY * 2 + rows * cellH;

  // --- Place nodes on a grid with jitter ---
  interface PlacedNode {
    id: string;
    row: number;
    col: number;
    x: number;
    y: number;
    type: RoomType;
  }

  const placed: PlacedNode[] = [];

  // Generate grid positions, then pick nodeCount of them
  const allCells: { row: number; col: number }[] = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      allCells.push({ row: r, col: c });
    }
  }
  shuffle(allCells, rng);
  const usedCells = allCells.slice(0, nodeCount);

  // Sort by row (top to bottom in world = row 0 is top)
  usedCells.sort((a, b) => a.row !== b.row ? a.row - b.row : a.col - b.col);

  for (let i = 0; i < usedCells.length; i++) {
    const cell = usedCells[i];
    const progress = cell.row / Math.max(rows - 1, 1);

    const baseX = padX + cell.col * cellW + cellW / 2;
    const baseY = padY + cell.row * cellH + cellH / 2;
    const jitterX = (rng() - 0.5) * cellW * 0.5;
    const jitterY = (rng() - 0.5) * cellH * 0.35;

    // Progress is inverted: row 0 = top of world = end of run (boss area)
    // row max = bottom of world = start area
    const invertedProgress = 1 - progress;

    placed.push({
      id: `n${i}`,
      row: cell.row,
      col: cell.col,
      x: Math.round(baseX + jitterX),
      y: Math.round(baseY + jitterY),
      type: pickRoomType(invertedProgress, rng),
    });
  }

  // --- Assign start and boss ---
  // Start: bottom-most node closest to horizontal centre
  const centreX = worldW / 2;
  const bottomRow = placed.reduce((max, n) => Math.max(max, n.row), 0);
  const bottomNodes = placed.filter(n => n.row === bottomRow);
  bottomNodes.sort((a, b) => Math.abs(a.x - centreX) - Math.abs(b.x - centreX));
  const startNode = bottomNodes[0];
  startNode.id = "start";
  startNode.type = "battle";

  // Boss: top-most node closest to horizontal centre
  const topRow = placed.reduce((min, n) => Math.min(min, n.row), Infinity);
  const topNodes = placed.filter(n => n.row === topRow && n !== startNode);
  topNodes.sort((a, b) => Math.abs(a.x - centreX) - Math.abs(b.x - centreX));
  const bossNode = topNodes[0];
  bossNode.id = "boss";
  bossNode.type = "boss";

  // --- Create MapNode objects ---
  for (const p of placed) {
    nodes.set(p.id, {
      id: p.id,
      type: p.type,
      connections: [],
      x: p.x,
      y: p.y,
    });
  }

  // --- Connect nodes ---
  // Strategy: connect each node to its nearest neighbours, ensuring
  // the graph is fully connected.

  function connect(a: string, b: string): void {
    const nodeA = nodes.get(a)!;
    const nodeB = nodes.get(b)!;
    if (!nodeA.connections.includes(b)) nodeA.connections.push(b);
    if (!nodeB.connections.includes(a)) nodeB.connections.push(a);
  }

  function dist(a: PlacedNode, b: PlacedNode): number {
    return Math.sqrt((a.x - b.x) ** 2 + (a.y - b.y) ** 2);
  }

  // For each node, connect to 2-4 nearest neighbours
  const targetConns = 3;
  for (const node of placed) {
    // Sort others by distance
    const others = placed
      .filter(o => o !== node)
      .sort((a, b) => dist(node, a) - dist(node, b));

    // Connect to nearest that we don't already have enough connections to
    const current = nodes.get(node.id)!;
    let added = 0;
    for (const other of others) {
      if (current.connections.length >= targetConns + 1) break;
      if (current.connections.includes(other.id)) { added++; continue; }

      // Don't connect nodes that are very far apart (>2.5 cells away)
      const d = dist(node, other);
      if (d > cellW * 2.5) break;

      connect(node.id, other.id);
      added++;
      if (added >= targetConns) break;
    }
  }

  // --- Ensure full connectivity via BFS ---
  // If there are disconnected islands, bridge them
  const visited = new Set<string>();
  const queue: string[] = [startNode.id];
  visited.add(startNode.id);

  while (queue.length > 0) {
    const id = queue.shift()!;
    const n = nodes.get(id)!;
    for (const connId of n.connections) {
      if (!visited.has(connId)) {
        visited.add(connId);
        queue.push(connId);
      }
    }
  }

  // Connect any unvisited nodes to their nearest visited node
  if (visited.size < nodes.size) {
    for (const p of placed) {
      if (visited.has(p.id)) continue;

      // Find nearest visited node
      let bestDist = Infinity;
      let bestId = startNode.id;
      for (const vId of visited) {
        const vp = placed.find(pp => pp.id === vId)!;
        const d = dist(p, vp);
        if (d < bestDist) {
          bestDist = d;
          bestId = vId;
        }
      }

      connect(p.id, bestId);
      visited.add(p.id);

      // BFS from this newly connected node to pick up its island
      const island: string[] = [p.id];
      while (island.length > 0) {
        const cur = island.shift()!;
        for (const connId of nodes.get(cur)!.connections) {
          if (!visited.has(connId)) {
            visited.add(connId);
            island.push(connId);
          }
        }
      }
    }
  }

  // --- Add a few random extra edges for web feel ---
  const extraEdges = Math.floor(nodeCount * 0.15);
  for (let i = 0; i < extraEdges; i++) {
    const a = placed[Math.floor(rng() * placed.length)];
    const others = placed
      .filter(o => o !== a && !nodes.get(a.id)!.connections.includes(o.id))
      .sort((x, y) => dist(a, x) - dist(a, y));

    if (others.length > 0) {
      // Pick one of the 3 nearest unconnected
      const pick = others[Math.floor(rng() * Math.min(3, others.length))];
      if (dist(a, pick) < cellW * 3) {
        connect(a.id, pick.id);
      }
    }
  }

  return { nodes, startNodeId: startNode.id, width: worldW, height: worldH };
}
